"use client";
import {
  createContext,
  ReactNode,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useUserContext } from "@/app/_hooks/useUserContext";
import {
  installSupportErrorListeners,
  recordSupportRoute,
} from "@/app/_helpers/supportTrace";
import { subscribeToSupportTickets } from "@/app/_firebase/support";
import { SupportTicket } from "@/app/@types/support";

interface SupportContextProps {
  tickets: SupportTicket[];
  isLoading: boolean;
  unreadCount: number;
  isOpen: boolean;
  // Ticket the drawer should show on open. null = list view.
  initialTicketId: string | null;
  open: (ticketId?: string) => void;
  close: () => void;
}

const SupportContext = createContext<SupportContextProps | undefined>(
  undefined
);

export const useSupportContext = () => {
  const context = useContext(SupportContext);
  if (!context) {
    throw new Error("useSupportContext must be used within a SupportProvider");
  }
  return context;
};

interface SupportProviderProps {
  children: ReactNode;
}

export const SupportProvider = ({ children }: SupportProviderProps) => {
  const user = useUserContext();
  const pathname = usePathname();

  useEffect(() => {
    installSupportErrorListeners();
  }, []);

  useEffect(() => {
    if (pathname) recordSupportRoute(pathname);
  }, [pathname]);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [initialTicketId, setInitialTicketId] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.uid) return;
    setIsLoading(true);
    const unsubscribe = subscribeToSupportTickets(
      user.uid,
      (data) => {
        setTickets(data);
        setIsLoading(false);
      },
      (error) => {
        console.error("Support tickets subscription failed", error);
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [user?.uid]);

  const unreadCount = useMemo(
    () => tickets.filter((t) => t.unreadForUser).length,
    [tickets]
  );

  const open = useCallback((ticketId?: string) => {
    setInitialTicketId(ticketId ?? null);
    setIsOpen(true);
  }, []);



  const close = useCallback(() => {
    setIsOpen(false);
    setInitialTicketId(null);
  }, []);

  const value = useMemo(
    () => ({
      tickets,
      isLoading,
      unreadCount,
      isOpen,
      initialTicketId,
      open,
      close,
    }),
    [tickets, isLoading, unreadCount, isOpen, initialTicketId, open, close]
  );

  return (
    <SupportContext.Provider value={value}>
      <Suspense fallback={null}>
        <SupportDeepLink onOpen={open} />
      </Suspense>
      {children}
    </SupportContext.Provider>
  );
};

// Deep link: ?support=<ticketId> opens the drawer to that ticket. Reacts to
// every navigation (bell links are client-side, emails are full loads), then
// strips the param so refreshes and back-nav do not reopen the drawer.
// Separate component: useSearchParams requires its own Suspense boundary.
const SupportDeepLink = ({
  onOpen,
}: {
  onOpen: (ticketId: string) => void;
}) => {
  const searchParams = useSearchParams();
  const ticketId = searchParams.get("support");

  useEffect(() => {
    if (!ticketId) return;
    onOpen(ticketId);
    const params = new URLSearchParams(window.location.search);
    params.delete("support");
    const search = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${search ? `?${search}` : ""}`
    );
  }, [ticketId, onOpen]);

  return null;
};
